import { Injectable, inject } from '@angular/core';
import { ExpenditureInterface } from '@app/interfaces/expenditure.interface';
import { FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IncomeInterface } from '@app/interfaces/income.interface';
import { BankAccountInterface } from '@app/interfaces/bank-account.interface';
import { ReplaySubject, Subject } from 'rxjs';
import { OptionInterface } from '@app/interfaces/option.interface';
import { OptionService } from '@app/services/option.service';
import { FormUtils } from '@app/utils/form-utils';
import { PropertyInterface } from '@app/interfaces/property.interface';
import { Property } from '@app/class/property.class';
import { ApplicantsAwareHousehold } from '@app/interfaces/household.interface';
import { MemberInterface } from '@app/interfaces/member.interface';
import { FormsIdentifierEnum } from '@app/enums/forms.enum';
import { BackendService } from '@app/services/backend.service';
import { IncomeConfig } from '@app/class/income.class';
import { PropertyTypeEnum } from '@app/enums/property-type.enum';
import { AddressInterface } from '@app/interfaces/address.interface';
import { FormModel } from 'ngx-mf';
import { PropertyIdWithStatus } from '@app/interfaces/property-id-with-status';
import { PropertyStatus } from '@app/interfaces/property-status';
import { UuidService } from '@app/services/uuid.service';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

interface HouseholdsFormModel {
    _id: string;
    applicants: string[];
    members: MemberInterface[];
    type: string;
}

export type HouseholdForm = FormModel<HouseholdsFormModel, { members: ['group'] }>;

export type MemberForm = FormModel<MemberInterface>;

export interface PropertyFormModel {
    _id: string;
    address: AddressInterface;
    applicants: any[];
    cadastreRegisterSeparately: boolean;
    commercialAreaPercentage: number;
    developerProject: string;
    housingUnitsCount: number;
    initialIndex: number;
    isDeveloperProject: boolean;
    isFunded: boolean;
    isOwned: boolean;
    isPledged: boolean;
    type: PropertyTypeEnum | string;
    value: number;
    createdAt?: Date;
}

export type PropertyForm = FormModel<PropertyFormModel, { applicants: ['group']; address: 'group' }>;

@Injectable({ providedIn: 'root' })
export class FormService {
    private readonly fb = inject(UntypedFormBuilder);
    private readonly optionService = inject(OptionService);
    private readonly backendService = inject(BackendService);
    private readonly uuidService = inject(UuidService);

    public filteredBanks: ReplaySubject<OptionInterface[]> = new ReplaySubject<OptionInterface[]>(1);
    public markAsTouchedSubject: Subject<void> = new Subject<void>();
    public rebuildValidatorsCustomInputsSubject: Subject<string> = new Subject<string>();
    public resetCustomInputsSubject: Subject<void> = new Subject<void>();

    public markAllAsTouched(form: any): void {
        form.markAllAsTouched();
        this.markAsTouchedSubject.next();
    }

    public rebuildValidatorsOnCustomInputs(formControlNames: string): void {
        this.rebuildValidatorsCustomInputsSubject.next(formControlNames);
    }

    public resetAllCustomInputs(): void {
        this.resetCustomInputsSubject.next();
    }

    public validOrFindError(form: any): boolean {
        if (!form.valid) {
            this.markAllAsTouched(form);
            this.scrollToError(false);
        }
        return form.valid;
    }

    public scrollTo(el: Element): void {
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    public scrollToError(isOverlay: boolean): boolean {
        let selectoraPipeline: string[] = ['app-root .ng-invalid[formControlName]', 'app-info-panel'];
        if (isOverlay) {
            selectoraPipeline = [
                '.cdk-overlay-container .ng-invalid[formControlName]',
                '.cdk-overlay-container app-info-panel',
            ];
        }

        const getValidityStatus = () => {
            const firstElementWithError =
                selectoraPipeline
                    .map((selector) => document.querySelector(selector))
                    .find((element) => element !== null) || null;

            this.scrollTo(firstElementWithError);
            return firstElementWithError;
        };

        setTimeout(() => {
            getValidityStatus();
        }, 10);

        return !!getValidityStatus();
    }

    public hasValidVisibleControls(form: any, isOverlay: boolean = false): boolean {
        this.markAllAsTouched(form);
        return !this.scrollToError(isOverlay);
    }

    public scrollToFirstUnfilled() {
        setTimeout(() => {
            const firstElementWithError =
                document.querySelector('button.orange.is-pulsing') ??
                document.querySelector('mat-expansion-panel.has-error');
            firstElementWithError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
    }

    public createExpenditureForm(expenditure: ExpenditureInterface = {} as ExpenditureInterface): UntypedFormGroup {
        const _id = expenditure?._id ?? this.uuidService.newMongoId();
        const visibleFormControls = {
            _id: new FormControl(_id),
            type: new FormControl(expenditure.type, Validators.required),
            payment: new FormControl(expenditure.payment ?? 20000, [Validators.min(1), Validators.required]),
            balance: new FormControl(expenditure.balance ?? 20000, [Validators.min(1), Validators.required]),
            limit: new FormControl(expenditure.limit ?? 20000, [Validators.min(1), Validators.required]),
            source: new FormGroup({
                type: new FormControl(expenditure?.source?.type ?? 'other', Validators.required),
                name: new FormControl(expenditure?.source?.name ?? null, Validators.required),
                bankCode: new FormControl('', Validators.required),
                countryCode: new FormControl('', Validators.required),
            }),
        };

        // only for store additional data from BE
        const dynamicFormControls = Object.keys(expenditure).reduce((formControls, key) => {
            if (!visibleFormControls[key]) {
                formControls[key] = new FormControl(expenditure[key]);
            }

            return formControls;
        }, {});

        return this.fb.group({
            ...visibleFormControls,
            ...dynamicFormControls,
        });
    }

    public createIncomeForm(income: IncomeInterface, incomeConfig: IncomeConfig): UntypedFormGroup {
        const _id = income?._id ?? this.uuidService.newMongoId();
        const form = this.fb.group({
            _id: [_id],
            type: [income?.type ?? null, Validators.required],
            location: [income?.location, Validators.required],
            locationCountry: [income?.locationCountry, Validators.required],
            locationAccount: [income?.locationAccount, Validators.required],
            costAccounting: [income?.costAccounting, Validators.required],
            currency: [income?.currency ?? 'CZK', Validators.required],
            rentalIncomeType: [income?.rentalIncomeType, Validators.required],
            flatRate: [income?.flatRate, Validators.required],
            amount: [income?.amount, [Validators.min(1), Validators.required]],
            source: this.fb.group({
                name: [income?.source?.name, Validators.required],
                createdAt: [income?.source?.createdAt, Validators.required],
                idNumber: [income?.source?.idNumber, [Validators.required, Validators.pattern('[0-9]{8}')]],
                sector: [income?.source?.sector, Validators.required],
                naceCode: [income?.source?.naceCode],
            }),
            fromDate: [income?.fromDate],
            contractTermPeriod: [income?.contractTermPeriod, Validators.required],
            untilDate: [income?.untilDate],
            stoppedInLastTwoYears: [income?.stoppedInLastTwoYears ?? false],
            isTrialPeriod: [income?.isTrialPeriod ?? false],
            isNoticePeriod: [income?.isNoticePeriod ?? false],
            isPaidToAccount: [income?.isPaidToAccount ?? false],
            isSubsequent: [income?.isSubsequent ?? false],
            employmentExtendedCount: [income?.employmentExtendedCount, Validators.required],
            employedAtOwnCompany: [income?.employedAtOwnCompany ?? false],
            professionType: [income?.professionType, Validators.required],
            taxReturn: this.fb.group({
                row101: [income?.taxReturn?.row101],
            }),
            shareholderFromDate: [income?.shareholderFromDate],
            shareInTheCompany: [
                income?.shareInTheCompany ?? 0,
                [Validators.required, Validators.min(0.001), Validators.max(100)],
            ],
        });
        form.get('untilDate').setValidators([
            FormUtils.conditionalValidator(
                () => FormUtils.isRequiredByType(incomeConfig.requiredOnTypes, 'untilDate', form.get('type').value),
                Validators.required
            ),
        ]);
        form.get('fromDate').setValidators([
            FormUtils.conditionalValidator(
                () => FormUtils.isRequiredByType(incomeConfig.requiredOnTypes, 'fromDate', form.get('type').value),
                Validators.required
            ),
        ]);
        form.get('source.idNumber').setValidators([
            FormUtils.conditionalValidator(
                () =>
                    FormUtils.isRequiredByType(incomeConfig.requiredOnTypes, 'source.idNumber', form.get('type').value),
                Validators.required
            ),
        ]);
        return form;
    }

    public createBankAccountForm(
        account: BankAccountInterface = {} as BankAccountInterface,
        bankAccounts: BankAccountInterface[] = [],
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        showMore: boolean = false
    ): UntypedFormGroup {
        const _id = account?._id ?? this.uuidService.newMongoId();
        const initialIndex = bankAccounts.length;
        const bankAccountIndex = account.initialIndex ?? initialIndex ?? null;

        let numberBefore;
        let numberAfter;
        if (account.number) {
            const accountNumberFractions = account.number.split('-');
            switch (accountNumberFractions.length) {
                case 1:
                    numberAfter = accountNumberFractions[0];
                    break;
                case 2:
                    numberBefore = accountNumberFractions[0];
                    numberAfter = accountNumberFractions[1];
                    break;
            }
        }

        const isHomeBankDisabled =
            account?.isHomeBank &&
            !bankAccounts?.filter((a) => a.initialIndex !== bankAccountIndex && a.isHomeBank)?.length;

        return this.fb.group({
            _id: [_id],
            initialIndex: [bankAccountIndex],
            numberBefore: [numberBefore ?? null],
            numberAfter: [numberAfter ?? null, [Validators.required]],
            number: [account?.number ?? null, [Validators.required]],
            bankCode: [account?.bankCode ?? null, Validators.required],
            bankCodeDisplay: [account?.bankCode ?? null, Validators.required],
            isHomeBank: new FormControl({ value: account?.isHomeBank ?? false, disabled: isHomeBankDisabled }),
        });
    }

    public createPropertyForm(
        property: PropertyInterface | PropertyFormModel = {} as PropertyInterface,
        properties: PropertyFormModel[] = [],
        propertyIdsWithStatus: PropertyIdWithStatus[]
    ): PropertyForm {
        const propertyConfig = new Property();
        const initialIndex = properties.length;
        const propertyIndex = property.initialIndex ?? initialIndex ?? null;

        if (propertyIdsWithStatus) {
            const propertyStatus: PropertyStatus[] =
                propertyIdsWithStatus.find((propertyIdWithStatus) => propertyIdWithStatus._id === property._id)
                    ?.status ?? [];
            property.isFunded = propertyStatus.includes('funded');
            property.isPledged = propertyStatus.includes('pledged');
        }

        const _id = property?._id ?? this.uuidService.newMongoId();
        const form = this.fb.group({
            _id: [_id],
            initialIndex: [propertyIndex],
            applicants: [[]],
            type: [property?.type, Validators.required],
            value: [property?.value ?? 3000000, [Validators.min(1), Validators.required]],
            developerProject: [property?.developerProject, Validators.required],
            isDeveloperProject: [property?.isDeveloperProject ?? false],
            isOwned: [property?.isOwned ?? false],
            isPledged: [property?.isPledged ?? false],
            isFunded: [property?.isFunded ?? false],
            address: this.fb.group({
                street: [property.address?.street],
                registryNumber: [property.address?.registryNumber],
                unitNumber: [property.address?.unitNumber, [Validators.required]],
                houseNumber: [property.address?.houseNumber],
                orientationNumber: [property.address?.orientationNumber],
                zipCode: [property.address?.zipCode, [Validators.required, Validators.pattern('[0-9]{5}')]],
                city: [property.address?.city, [Validators.required]],
                cadastralTerritory: [property?.address?.cadastralTerritory],
                parcelNumber: [property?.address?.parcelNumber, [Validators.required]],
            }),
            housingUnitsCount: [property?.housingUnitsCount, [Validators.min(1), Validators.required]],
            cadastreRegisterSeparately: [property?.cadastreRegisterSeparately],
            commercialAreaPercentage: [property?.commercialAreaPercentage || 0, [Validators.required]],
            createdAt: [property?.createdAt || null],
        });
        form.get('address.street').setValidators([
            FormUtils.conditionalValidator(
                () =>
                    FormUtils.isRequiredByType(
                        propertyConfig.requiredOnTypes,
                        'address.street',
                        form.get('type').value
                    ),
                Validators.required
            ),
        ]);
        form.get('address.houseNumber').setValidators([
            FormUtils.conditionalValidator(
                () =>
                    !form.get('isDeveloperProject').value &&
                    FormUtils.isRequiredByType(
                        propertyConfig.requiredOnTypes,
                        'address.houseNumber',
                        form.get('type').value
                    ),
                Validators.required
            ),
        ]);
        // address.orientationNumber is not mandatory
        form.get('address.cadastralTerritory').setValidators([
            FormUtils.conditionalValidator(
                () =>
                    FormUtils.isRequiredByType(
                        propertyConfig.requiredOnTypes,
                        'address.cadastralTerritory',
                        form.get('type').value
                    ),
                Validators.required
            ),
        ]);

        return form;
    }

    public createHouseholdForm(household: ApplicantsAwareHousehold = {} as ApplicantsAwareHousehold): HouseholdForm {
        const householdForm = new FormGroup({
            _id: new FormControl<string>(household._id),
            applicants: new FormControl<string[]>(household.applicants ?? []),
            members: new FormArray<MemberForm>([]),
            type: new FormControl<string>(household.type, Validators.required),
        });

        if (household.members?.length) {
            for (const member of household.members) {
                (householdForm.get('members') as FormArray).push(this.createMemberForm(member));
            }
        }

        return householdForm;
    }

    public createMemberForm(householdMember: MemberInterface): MemberForm {
        return new FormGroup({
            type: new FormControl(householdMember?.type ?? 'withIncome', Validators.required),
            age: new FormControl(householdMember?.age ?? 1, {
                validators: [Validators.required, Validators.min(0), Validators.max(120)],
            }),
        });
    }

    public resetBankList() {
        this.filteredBanks.next(this.optionService.getListByType('bank'));
    }

    public loadFormFromLocalStorage(
        patchValuesWhenFound: boolean,
        mortgage: MortgageInterface,
        form: UntypedFormGroup,
        autoSaveID: FormsIdentifierEnum,
        subFormID: string = ''
    ): any {
        if (!this.backendService.lsIsAvailable) {
            console.log('Local storage is not available. Auto-loading is off');
            return false;
        }
        if (!mortgage) {
            console.log('Mortgage is missing, cannot auto load form.');
            return false;
        }
        const index = `mortgage_${mortgage._id}`;
        const formAutoSaveObject = JSON.parse(localStorage.getItem(`autosave_${index}`));
        if (formAutoSaveObject) {
            const foundAutoSave = formAutoSaveObject.forms?.find(
                (a) => a.autoSaveID === autoSaveID && a.subFormID === subFormID
            );
            if (foundAutoSave) {
                const offerUpdatedTime = new Date(mortgage.updatedAt).getTime();
                const autoSaveUpdatedTime = new Date(foundAutoSave.updatedAt).getTime();
                let finalObject;
                let isOutdated;
                if (offerUpdatedTime < autoSaveUpdatedTime) {
                    finalObject = foundAutoSave.form;
                    isOutdated = false;
                }
                if (patchValuesWhenFound) {
                    this.loadAndUpdateForm(form, finalObject);
                } else {
                    return {
                        isOutdated,
                        form: finalObject,
                    };
                }
            }
        }
    }

    public loadAndUpdateForm(form: UntypedFormGroup, finalObject: any) {
        form.patchValue(finalObject, { emitEvent: false });
        this.markAllAsTouched(form);
        form.updateValueAndValidity({ emitEvent: false });
    }

    public saveFormToLocalStorage(
        mortgage: MortgageInterface,
        form: UntypedFormGroup,
        autoSaveID: FormsIdentifierEnum,
        subFormID: string = ''
    ): void {
        if (!this.backendService.lsIsAvailable) {
            console.log('Local storage is not available. Auto-saving is off');
            return;
        }
        if (!mortgage) {
            console.log('Offer ID is missing, cannot auto save form.');
            return;
        }
        const index = `mortgage_${mortgage._id}`;
        const formAutoSaveObject = JSON.parse(localStorage.getItem(`autosave_${index}`)) ?? {
            forms: [],
        };
        const formToSave = {
            autoSaveID,
            subFormID,
            form: form.getRawValue(),
            updatedAt: new Date(),
        };
        const currentFormIndex = formAutoSaveObject.forms?.findIndex(
            (a) => a.autoSaveID === autoSaveID && a.subFormID === subFormID
        );
        if (currentFormIndex !== -1) {
            formAutoSaveObject.forms[currentFormIndex] = formToSave;
        } else {
            if (formAutoSaveObject?.forms) {
                formAutoSaveObject.forms.push(formToSave);
            } else {
                formAutoSaveObject.forms = [formToSave];
            }
        }
        localStorage.setItem(`autosave_${index}`, JSON.stringify(formAutoSaveObject));
    }
}
